import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { filterPlannerPool } from './plannerListFilters';

const DEFAULT_PAGE_SIZE = 5;
const DEFAULT_MOCK_MS = 450;

/** Filtered planner list with infinite-scroll style paging over the filtered pool. */
export function usePlannerFilteredList({
  source,
  distanceLimitMi,
  containerSearch,
  pageSize = DEFAULT_PAGE_SIZE,
  mockMs = DEFAULT_MOCK_MS,
}) {
  const filteredPool = useMemo(
    () => filterPlannerPool(source, distanceLimitMi, containerSearch),
    [source, distanceLimitMi, containerSearch]
  );

  const [items, setItems] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadingLock = useRef(false);
  const poolRef = useRef(filteredPool);
  poolRef.current = filteredPool;

  useLayoutEffect(() => {
    setItems(filteredPool.slice(0, pageSize));
  }, [filteredPool, pageSize]);

  useEffect(() => {
    setHasMore(items.length < filteredPool.length);
  }, [items.length, filteredPool.length]);

  const loadNextPageRef = useRef(null);
  const loadNextPage = useCallback(async () => {
    if (loadingLock.current || !hasMore) return;
    loadingLock.current = true;
    setLoadingMore(true);
    try {
      await new Promise((r) => setTimeout(r, mockMs));
      const pool = poolRef.current;
      setItems((prev) => {
        const nextSlice = pool.slice(prev.length, prev.length + pageSize);
        if (nextSlice.length === 0) return prev;
        return [...prev, ...nextSlice];
      });
    } finally {
      setLoadingMore(false);
      loadingLock.current = false;
    }
  }, [hasMore, pageSize, mockMs]);

  loadNextPageRef.current = loadNextPage;

  return {
    items,
    loadingMore,
    hasMore,
    loadNextPageRef,
    filteredTotal: filteredPool.length,
  };
}
